import { RealtimeChannel, RealtimeClient } from "@supabase/realtime-js";
import type { Claims } from "../../../../server/supabase/hooks/get-claims";
import type {
  RoundaboutBuildArgs,
  RoundaboutBuildResult,
  RoundaboutEventPayload,
  RoundaboutOnSyncIn,
  RoundaboutRoomUser,
} from "./roundabout-markings";

const buildRoom = (
  momentId: string,
  user: Claims,
  getAccessToken: () => Promise<string>,
): RealtimeChannel => {
  const supabaseUrl = user["http://changeengine.com/supabase_url"];
  const supabaseAnonKey = user["http://changeengine.com/supabase_anon_key"];
  const userSub = user.sub;

  const url = new URL(supabaseUrl);
  url.pathname = "/realtime/v1";

  const realtime = new RealtimeClient(url.href, {
    params: {
      apikey: supabaseAnonKey,
    },
    accessToken: getAccessToken,
  });

  return realtime.channel(momentId, {
    config: {
      presence: {
        // We only support 1 editing session per user
        // so we can use the sub as the key and stay sane.
        key: userSub,
      },
    },
  });
};

const buildSyncOutHandler = (room: RealtimeChannel) => async (event: RoundaboutEventPayload) => {
  try {
    // For syncout events, phoenix doesn't seem to include a reference so the adapter thinks the event times out.
    // So we are only watching for `error` responses.
    // the try catch is for any unforeseen error types.
    const response = await room.track(event);
    if (response === "error") {
      throw new Error(`received response error`);
    }
  } catch (error) {
    console.error("Failed to track", error);
  }
};

const buildPresenceCallback = (onSyncIn: RoundaboutOnSyncIn, room: RealtimeChannel) => {
  return () => {
    const roomState = room.presenceState<RoundaboutRoomUser>();
    onSyncIn(roomState);
  };
};

const buildCleanup = (subscription: RealtimeChannel, room: RealtimeChannel) => () => {
  // The docs aren't exactly clear the difference between these
  void subscription.unsubscribe().catch(console.warn);
  void room.untrack().catch(console.warn);
};

export const build = ({
  getAccessToken,
  momentId,
  onSyncIn,
  user,
}: RoundaboutBuildArgs): RoundaboutBuildResult => {
  const builtRoom = buildRoom(momentId, user, getAccessToken);

  const presenceCallback = buildPresenceCallback(onSyncIn, builtRoom);

  const subscription = builtRoom.on("presence", { event: "sync" }, presenceCallback).subscribe();

  const cleanup = buildCleanup(subscription, builtRoom);

  const handleSyncOut = buildSyncOutHandler(builtRoom);

  return { cleanup, handleSyncOut };
};
